import { useEffect, useState } from "react";
import { Session } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useDailyMood } from "@/hooks/useDailyMood";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

const EMOJIS = [
  { score: 1, emoji: "😢", label: "Very Low", message: "I hear you're having a tough day. Remember, it's okay to not be okay." },
  { score: 2, emoji: "😕", label: "Low", message: "Things might be difficult right now. Let's work through this together." },
  { score: 3, emoji: "😐", label: "Neutral", message: "Taking time to check in with yourself is a great practice." },
  { score: 4, emoji: "🙂", label: "Good", message: "It's wonderful to hear you're doing well!" },
  { score: 5, emoji: "😄", label: "Great", message: "That's fantastic! Let's keep this positive energy going!" },
];

export function DailyMoodCheckIn({ session }: { session: Session | null }) {
  const [open, setOpen] = useState(false);
  const [selectedMood, setSelectedMood] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const { saveDailyMood, todaysMood, isLoading } = useDailyMood();
  const { toast } = useToast();

  useEffect(() => {
    if (session && !todaysMood && !isLoading) {
      setOpen(true);
    }
  }, [session, todaysMood, isLoading]);

  const handleMoodSelect = async (score: number, emoji: string) => {
    setSelectedMood(score);
  };

  const handleSave = async () => {
    if (selectedMood === null) {
      toast({
        title: "Please select a mood",
        description: "Choose how you're feeling today",
        variant: "destructive",
      });
      return;
    }

    try {
      const selectedEmoji = EMOJIS.find(e => e.score === selectedMood)?.emoji || "";
      await saveDailyMood({
        mood_score: selectedMood,
        emoji: selectedEmoji,
        note,
        date: new Date().toISOString().split('T')[0],
      });
      
      toast({
        title: "Mood saved",
        description: EMOJIS.find(e => e.score === selectedMood)?.message,
      });
      
      setOpen(false);
      setSelectedMood(null);
      setNote("");
    } catch (error) {
      console.error('Error saving mood:', error);
      toast({
        title: "Error saving mood",
        description: "Please try again later",
        variant: "destructive",
      });
    }
  };

  if (!session) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl">
            How are you feeling today?
          </DialogTitle>
          <DialogDescription className="text-center">
            Take a moment to check in with yourself
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-center space-x-4 py-6">
          {EMOJIS.map(({ score, emoji, label }) => (
            <Button
              key={score}
              variant="ghost"
              className={cn(
                "flex flex-col items-center p-4 hover:bg-primary/10",
                selectedMood === score && "bg-primary/20"
              )}
              onClick={() => handleMoodSelect(score, emoji)}
            >
              <span className="text-3xl mb-2">{emoji}</span>
              <span className="text-xs text-muted-foreground">{label}</span>
            </Button>
          ))}
        </div>
        
        {selectedMood && (
          <div className="space-y-4">
            <DialogDescription className="text-center text-sm">
              {EMOJIS.find(e => e.score === selectedMood)?.message}
            </DialogDescription>
            <Textarea
              placeholder="Would you like to add any notes about how you're feeling? (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
        )}
        
        <DialogFooter className="sm:justify-center">
          <Button 
            onClick={handleSave}
            className="w-full sm:w-auto"
            disabled={selectedMood === null}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}